import { tKey } from 'ember-intl';
import {
  OTB_SS_ENTRY_POINTS,
  type OTBSSEntryPoint,
} from 'lumen/utils/constants/otb-selfservice-constants';

export function getTranslationKeysForBanner(
  entryPoint: OTBSSEntryPoint,
  isMarketBenchmarkEligible?: boolean,
): {
  message: string;
  title: string | undefined;
} {
  switch (entryPoint) {
    // Market Insight OTB SS entry point translations
    case OTB_SS_ENTRY_POINTS.MI_OCCUPANCY_CHART: {
      if (isMarketBenchmarkEligible) {
        return {
          message: tKey('market_insight.otb_ss.empty_state.paragraph'),
          title: tKey(
            'market_insight.otb_ss.empty_state.chart.occupancy.title',
          ),
        };
      }

      return {
        message: tKey(
          'market_insight.otb_ss.empty_state.my_otb_only.paragraph',
        ),
        title: tKey('market_insight.otb_ss.empty_state.chart.occupancy.title'),
      };
    }

    case OTB_SS_ENTRY_POINTS.OD_ALERTS: {
      if (isMarketBenchmarkEligible) {
        return {
          message: tKey(
            'overview_dashboard.otb_ss.empty_state.kpi.performance.paragraph',
          ),
          title: tKey('overview_dashboard.otb_ss.kpi.alerts.title'),
        };
      }

      return {
        message: tKey(
          'overview_dashboard.otb_ss.empty_state.my_otb_only.kpi.performance.paragraph',
        ),
        title: tKey('overview_dashboard.otb_ss.my_otb_only.kpi.alerts.title'),
      };
    }

    case OTB_SS_ENTRY_POINTS.OD_EVENTS: {
      if (isMarketBenchmarkEligible) {
        return {
          message: tKey(
            'overview_dashboard.otb_ss.empty_state.kpi.performance.paragraph',
          ),
          title: tKey(
            'overview_dashboard.otb_ss.empty_state.kpi.events_holidays.title',
          ),
        };
      }

      return {
        message: tKey(
          'overview_dashboard.otb_ss.empty_state.my_otb_only.kpi.performance.paragraph',
        ),
        title: tKey(
          'overview_dashboard.otb_ss.empty_state.kpi.events_holidays.title',
        ),
      };
    }

    case OTB_SS_ENTRY_POINTS.OD_KEY_DATES: {
      if (isMarketBenchmarkEligible) {
        return {
          message: tKey(
            'overview_dashboard.otb_ss.empty_state.kpi.performance.paragraph',
          ),
          title: tKey('overview_dashboard.otb_ss.kpi.keydates.title'),
        };
      }

      return {
        message: tKey(
          'overview_dashboard.otb_ss.empty_state.my_otb_only.kpi.performance.paragraph',
        ),
        title: tKey('overview_dashboard.otb_ss.kpi.keydates.title'),
      };
    }

    // Overview Dashboard OTB SS banner translations
    case OTB_SS_ENTRY_POINTS.OD_OCCUPANCY: {
      if (isMarketBenchmarkEligible) {
        return {
          message: tKey(
            'overview_dashboard.otb_ss.empty_state.kpi.occupancy.paragraph',
          ),
          title: tKey(
            'overview_dashboard.otb_ss.empty_state.kpi.occupancy.title',
          ),
        };
      }

      return {
        message: tKey(
          'overview_dashboard.otb_ss.empty_state.my_otb_only.kpi.occupancy.paragraph',
        ),
        title: tKey(
          'overview_dashboard.otb_ss.empty_state.my_otb_only.kpi.occupancy.title',
        ),
      };
    }

    case OTB_SS_ENTRY_POINTS.OD_PERFORMANCE_RATES_CHART: {
      if (isMarketBenchmarkEligible) {
        return {
          message: tKey(
            'overview_dashboard.otb_ss.empty_state.kpi.performance.paragraph',
          ),
          title: tKey(
            'overview_dashboard.otb_ss.empty_state.kpi.performance.title',
          ),
        };
      }

      return {
        message: tKey(
          'overview_dashboard.otb_ss.empty_state.my_otb_only.kpi.performance.paragraph',
        ),
        title: tKey(
          'overview_dashboard.otb_ss.empty_state.my_otb_only.kpi.performance.title',
        ),
      };
    }

    case OTB_SS_ENTRY_POINTS.OD_SETTINGS: {
      if (isMarketBenchmarkEligible) {
        return {
          message: tKey(
            'overview_dashboard.otb_ss.empty_state.kpi.performance.paragraph',
          ),
          title: tKey('overview_dashboard.otb_ss.settings.title'),
        };
      }

      return {
        message: tKey(
          'overview_dashboard.otb_ss.empty_state.my_otb_only.kpi.performance.paragraph',
        ),
        title: tKey('overview_dashboard.otb_ss.settings.title'),
      };
    }

    // Rate Insight and Pricing Assistant OTB SS banner translations
    case OTB_SS_ENTRY_POINTS.PRICING_ASSISTANT_CALENDAR:
    case OTB_SS_ENTRY_POINTS.RATES_CALENDAR:
    case OTB_SS_ENTRY_POINTS.RATES_CALENDAR_SETTINGS_MODAL:
    case OTB_SS_ENTRY_POINTS.RATES_CHART:
    case OTB_SS_ENTRY_POINTS.RATES_EVOLUTION_CHART:
    case OTB_SS_ENTRY_POINTS.RATES_TABLE:
    case OTB_SS_ENTRY_POINTS.RECOMMENDATIONS_SETTINGS: {
      if (isMarketBenchmarkEligible) {
        return {
          message: tKey('rate_insight.otb_ss.empty_state.paragraph'),
          title: tKey('rate_insight.otb_ss.empty_state.title'),
        };
      }

      return {
        message: tKey('rate_insight.otb_ss.empty_state.my_otb_only.paragraph'),
        title: tKey('rate_insight.otb_ss.empty_state.title'),
      };
    }

    case OTB_SS_ENTRY_POINTS.RATES_PACE_CHART: {
      if (isMarketBenchmarkEligible) {
        return {
          message: tKey('rate_insight.otb_ss.empty_state.paragraph'),
          title: tKey('rate_insight.otb_ss.empty_state.pace_chart.title'),
        };
      }

      return {
        message: tKey('rate_insight.otb_ss.empty_state.my_otb_only.paragraph'),
        title: tKey(
          'rate_insight.otb_ss.empty_state.my_otb_only.pace_chart.title',
        ),
      };
    }

    case OTB_SS_ENTRY_POINTS.RENTAL_EVOLUTION_CHART: {
      return {
        message: tKey('short_term_rental.otb_ss.empty_state.paragraph'),
        title: tKey('short_term_rental.otb_ss.empty_state.title'),
      };
    }

    default: {
      return {
        message: tKey(
          'overview_dashboard.otb_ss.empty_state.my_otb_only.kpi.performance.paragraph',
        ),
        title: undefined,
      };
    }
  }
}
